import {
  buildCapsules,
  vec,
  type Capsule,
  type CapsuleEnd,
  type HumanoidModel,
  type NodeKey,
  type Skeleton,
  type Vec3,
} from '@/domain/hs1'

/**
 * Ebene 2, zweiter Teil — das Bauteil im eigenen Rahmen.
 *
 * WAS BISHER FEHLTE. Ein Bauteil existierte nur als Kapsel zwischen zwei
 * WELTPUNKTEN: „von der Hüfte zum Knie". Das reicht für eine Abstandsprüfung
 * und für nichts sonst. Es sagt nicht, auf welchem Glied das Teil sitzt, woher
 * seine Zahlen kommen, und es lässt sich nicht zeichnen, ohne den ganzen
 * Roboter mitzurechnen.
 *
 * EIN BAUTEIL HAT EINEN EIGENEN RAHMEN, und das ist der ganze Unterschied. Der
 * Ursprung liegt an seiner Aufhängung, die Achse zeigt zum anderen Ende, und
 * alles am Teil ist in diesem Rahmen beschrieben. Damit
 *
 *   – lässt es sich einzeln zeichnen, ohne Pose und ohne Gang,
 *   – ändert eine verschobene Gelenkachse seine Lage und nicht seine Form,
 *   – und eine Hüllkurve aus dem CAD tritt später an die Stelle der
 *     gerechneten, ohne dass irgendetwas anderes davon weiss.
 *
 * DER TRÄGER IST DER KNOTEN, AUF DEM ES SITZT — nicht der, zu dem es reicht.
 * Der Oberschenkel sitzt an der Hüfte und reicht zum Knie; dreht das Knie, dreht
 * sich der Oberschenkel NICHT mit. Wer die Zugehörigkeit am anderen Ende
 * festmacht, bekommt ein Teil, das sich mit dem falschen Gelenk mitbewegt.
 *
 * DIE HERKUNFT STEHT AM TEIL UND NICHT IN EINER ZWEITEN LISTE. Solange eine
 * Zahl geschätzt ist, muss man das an ihr sehen — sonst liest sich eine
 * anthropometrische Annahme wie eine gewogene Baugruppe.
 */

/**
 * Woher die Zahlen eines Bauteils stammen.
 *
 * Die Reihenfolge ist eine Rangfolge: was aus der Konstruktion kommt, schlägt
 * die Schätzung, und was jemand von Hand einträgt, schlägt beides — weil es
 * meist ein Wägeergebnis ist.
 */
export type Provenance = 'schaetzung' | 'plm' | 'hand'

export const PROVENANCE_LABELS: Record<Provenance, string> = {
  schaetzung: 'geschätzt',
  plm: 'aus der Konstruktion',
  hand: 'von Hand',
}

/** Ein Punkt in der Zeichenebene des Bauteils, Meter — wie der ganze Kern. */
export interface Point2 {
  x: number
  y: number
}

export interface Part {
  key: string
  label: string
  /**
   * Der Knoten des ACHSBAUMS, auf dem das Teil sitzt.
   *
   * ZWEI VOKABULARE, UND SIE DECKEN SICH NICHT. Die Kinematik kennt Rahmen —
   * „hip" ist der Hüftrahmen, in dem alle drei Hüftachsen schon gedreht haben.
   * Der Achsbaum kennt Punkte, und die Hüfte ist dort DREI Knoten: `hipG`,
   * `hipR`, `hipN`. Wer den Rahmennamen als Knoten nimmt, findet am Achsbaum
   * nichts und zeigt eine leere Liste — genau das ist beim ersten Durchlauf
   * passiert.
   */
  carrier: string
  /** Der kinematische Rahmen, in dem Ursprung und Reichweite stehen. */
  frame: NodeKey
  side?: Capsule['side']
  group: Capsule['group']
  /** Wo das Teil an seinem Träger hängt, im Rahmen des Trägers, m. */
  origin: Vec3
  /**
   * Wohin es reicht, im Rahmen des Trägers, m.
   *
   * Der Endpunkt liegt oft an einem ANDEREN Knoten — der Oberschenkel endet am
   * Knie. Er wird trotzdem im Rahmen des Trägers geführt: in dem steht er still,
   * und genau das macht ihn zur Bauteilgrösse statt zur Posengrösse.
   */
  reach: Vec3
  /** Länge des Teils entlang seiner Achse, m. */
  length: number
  /** Halbe Dicke, m. */
  radius: number
  provenance: Provenance
  /** Masse, kg — soweit bekannt. */
  mass: number | null
}

/**
 * Baut die Bauteile aus dem Skelett.
 *
 * AUS DENSELBEN KAPSELN WIE DIE FREIGANGSPRÜFUNG. Eine zweite Bauteilliste wäre
 * eine zweite Wahrheit über dieselbe Maschine — und die erste, die veraltet,
 * wäre die, die niemand ansieht. Was hier dazukommt, ist nicht ein anderer Satz
 * Teile, sondern eine andere SICHT auf denselben: aus zwei Weltenden wird ein
 * Rahmen mit Ursprung, Achse und Länge.
 */
export function parts(
  model: HumanoidModel,
  skeleton: Skeleton,
  masses: Partial<Record<string, { mass: number; provenance: Provenance }>> = {},
): Part[] {
  return buildCapsules(skeleton, model.scale).map((capsule): Part => {
    const origin = endOffset(capsule.a)
    const reach = reachOf(capsule, skeleton)
    const known = masses[capsule.key]
    return {
      key: capsule.key,
      label: capsule.label,
      carrier: CARRIER[capsule.a.node] ?? capsule.a.node,
      frame: capsule.a.node,
      side: capsule.side,
      group: capsule.group,
      origin,
      reach,
      length: Math.hypot(reach.x - origin.x, reach.y - origin.y, reach.z - origin.z),
      radius: capsule.radius,
      provenance: known?.provenance ?? 'schaetzung',
      mass: known?.mass ?? null,
    }
  })
}

/**
 * Welcher Knoten des Achsbaums einen kinematischen Rahmen trägt.
 *
 * Nur die Rahmen, die im Achsbaum mehrere Knoten haben, stehen hier. Für alle
 * anderen heisst der Knoten wie der Rahmen — dann greift der Vorgabewert.
 *
 * Beim Bein ist es die LETZTE Achse der Kette: der Oberschenkel hängt unter der
 * Nickachse, nicht unter der Gierachse. Wer ihn an die erste hängt, bekommt ein
 * Teil, das beim Abspreizen stehen bleibt.
 */
const CARRIER: Partial<Record<NodeKey, string>> = {
  hip: 'hipN',
  trunk: 'chest',
  head: 'headC',
}

/** Der Versatz eines Kapselendes gegen seinen Knoten. */
const endOffset = (end: CapsuleEnd): Vec3 => end.offset

/**
 * Wohin das Teil reicht, im Rahmen seines Trägers.
 *
 * Sitzen beide Enden am selben Knoten, ist es der blosse Versatz. Sitzen sie an
 * verschiedenen, kommt der Abstand der Knoten in der GRUNDSTELLUNG dazu — und
 * das ist der Punkt: in der Grundstellung ist der Abstand eine Bauteillänge und
 * keine Pose. Der Oberschenkel ist 0,42 m lang, ob das Knie gebeugt ist oder
 * nicht.
 */
function reachOf(capsule: Capsule, skeleton: Skeleton): Vec3 {
  const a = capsule.a
  const b = capsule.b
  if (a.node === b.node) return b.offset
  const span = restSpan(a.node, b.node, capsule.side, skeleton)
  return vec(b.offset.x + span.x, b.offset.y + span.y, b.offset.z + span.z)
}

/**
 * Der Abstand zweier benachbarter Knoten in der Grundstellung.
 *
 * Er steht als Tabelle und nicht als Posenrechnung: eine Pose bringt Winkel
 * mit, und ein Bauteil hat keine. Was hier fehlt, bekommt einen Versatz null —
 * dann liegt das Teil im Ursprung seines Trägers, und das ist sichtbar falsch
 * statt unsichtbar falsch.
 */
function restSpan(
  from: NodeKey,
  to: NodeKey,
  side: Capsule['side'],
  skeleton: Skeleton,
): Vec3 {
  const limb = skeleton.limb[side ?? 'R']
  /* Die Glieder hängen in der Grundstellung senkrecht nach unten: der Abstand
     ist eine Länge entlang der eigenen Achse. */
  if (from === 'hip' && to === 'knee') return vec(0, -limb.thigh, 0)
  if (from === 'knee' && to === 'ankle') return vec(0, -limb.shank, 0)
  if (from === 'shoulder' && to === 'elbow') return vec(0, -limb.upperArm, 0)
  if (from === 'elbow' && to === 'wrist') return vec(0, -limb.forearm, 0)
  /* Der Fuss knickt am Zehengrundgelenk ab; sein Ort im Fusssystem ist die
     Bauteillänge zwischen Sprunggelenk und Zehen. */
  if (from === 'ankle' && to === 'mtp') return vec(limb.mtp.x, limb.mtp.y, limb.mtp.z)
  return vec(0, 0, 0)
}

/* -------------------------------------------------------------------------
   Die Hüllkurve
   ------------------------------------------------------------------------- */

/** Punkte je Rundung an einem Ende. */
const CAP_STEPS = 10

/**
 * Der Umriss eines Bauteils in seiner eigenen Zeichenebene.
 *
 * EINE KAPSEL VON DER SEITE: zwei Geraden und zwei Halbkreise. Der Ursprung
 * liegt an der Aufhängung, die Achse zeigt nach unten zum anderen Ende — die
 * Zeichenebene ist damit für jedes Teil dieselbe, egal wo es im Roboter sitzt.
 *
 * DAS IST DIE VORLÄUFIGE HÜLLE. Sie kommt aus Länge und Radius, also aus zwei
 * Zahlen. Sobald aus dem CAD ein echter Umriss zurückkommt, tritt er an dieselbe
 * Stelle, und nichts anderes muss sich ändern — das ist der Grund, warum sie
 * hier eine eigene Funktion ist und nicht im Zeichencode steht.
 */
export function outline(part: Part): Point2[] {
  const r = part.radius
  const l = part.length
  const points: Point2[] = []

  /* Rundung am oberen Ende, von rechts über oben nach links. */
  for (let k = 0; k <= CAP_STEPS; k++) {
    const a = (k / CAP_STEPS) * Math.PI
    points.push({ x: r * Math.cos(a), y: -r * Math.sin(a) })
  }
  /* Rundung am unteren Ende, von links über unten nach rechts. */
  for (let k = 0; k <= CAP_STEPS; k++) {
    const a = Math.PI + (k / CAP_STEPS) * Math.PI
    points.push({ x: r * Math.cos(a), y: l - r * Math.sin(a) })
  }
  return points
}

/** Der Umriss als SVG-Pfad, geschlossen. */
export function outlinePath(part: Part): string {
  const pts = outline(part)
  if (pts.length === 0) return ''
  return `${pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join('')}Z`
}

/** Die Bauteile, die auf einem Knoten sitzen. */
export const partsOn = (list: Part[], node: string): Part[] =>
  list.filter((p) => p.carrier === node)
